import { type VendorItem } from '@/common/types/vendor.type';
import { create, type StateCreator } from 'zustand';
import { persist, type PersistOptions } from 'zustand/middleware';

export interface CartItemProduct {
  productId: number;
  quantity: number;
  productVariantId: number;
}

export interface CartItem {
  vendor: VendorItem;
  products: CartItemProduct[];
}

interface IncreaseItem {
  vendor: VendorItem;
  product: Omit<CartItemProduct, 'quantity'>;
}

interface DecreaseItem {
  vendor: VendorItem;
  product: Omit<CartItemProduct, 'quantity'>;
}

export interface CartStore {
  items: CartItem[];
  increaseItem: (item: IncreaseItem) => void;
  decreaseItem: (item: DecreaseItem) => void;
  removeAllProducts: (vendorId: number) => void;
  removeProduct: (vendorId: number, productVariantId: number) => void;
  reset: () => void;
}

type CartPersist = (
  config: StateCreator<CartStore>,
  options: PersistOptions<CartStore>
) => StateCreator<CartStore>;

export const useCartStore = create<CartStore, []>(
  (persist as CartPersist)(
    (set): CartStore => ({
      items: [],
      increaseItem: (item) => {
        set((state) => {
          const cartItemIndex = state.items.findIndex(
            (i) => i.vendor.id === item.vendor.id
          );

          if (cartItemIndex > -1) {
            const productItemIndex = state.items[
              cartItemIndex
            ].products.findIndex(
              (p) => p.productVariantId === item.product.productVariantId
            );

            if (productItemIndex > -1) {
              const updatedItems = [...state.items];
              const updatedProducts = [...updatedItems[cartItemIndex].products];
              updatedProducts[productItemIndex] = {
                ...updatedProducts[productItemIndex],
                quantity: updatedProducts[productItemIndex].quantity + 1,
              };
              updatedItems[cartItemIndex] = {
                ...updatedItems[cartItemIndex],
                products: updatedProducts,
              };
              return { items: updatedItems };
            } else {
              return {
                items: state.items.map((cartItem, index) =>
                  index === cartItemIndex
                    ? {
                        ...cartItem,
                        products: [
                          ...cartItem.products,
                          { quantity: 1, ...item.product },
                        ],
                      }
                    : cartItem
                ),
              };
            }
          } else {
            const hasOtherVendorProducts = state.items.some(
              (cart) =>
                cart.vendor.id !== item.vendor.id && cart.products.length > 0
            );

            if (hasOtherVendorProducts) {
              return { items: state.items };
            }

            return {
              items: [
                {
                  vendor: item.vendor,
                  products: [{ quantity: 1, ...item.product }],
                },
              ],
            };
          }
        });
      },
      decreaseItem: (item) => {
        set((state) => {
          const cartItemIndex = state.items.findIndex(
            (i) => i.vendor.id === item.vendor.id
          );
          if (cartItemIndex > -1) {
            const productItemIndex = state.items[
              cartItemIndex
            ].products.findIndex(
              (p) => p.productVariantId === item.product.productVariantId
            );
            if (productItemIndex > -1) {
              const updatedItems = [...state.items];
              const updatedProducts = [...updatedItems[cartItemIndex].products];
              if (updatedProducts[productItemIndex].quantity > 1) {
                updatedProducts[productItemIndex] = {
                  ...updatedProducts[productItemIndex],
                  quantity: updatedProducts[productItemIndex].quantity - 1,
                };
              } else {
                updatedProducts.splice(productItemIndex, 1);
              }
              updatedItems[cartItemIndex] = {
                ...updatedItems[cartItemIndex],
                products: updatedProducts,
              };
              return { items: updatedItems };
            }
          }
          return { items: state.items };
        });
      },
      removeAllProducts: (vendorId) => {
        set((state) => {
          const items = state.items;
          const cartItemIndex = items.findIndex(
            (i) => i.vendor.id === vendorId
          );
          if (cartItemIndex !== -1) {
            items[cartItemIndex].products = [];
          }
          return { items: [] };
        });
      },
      removeProduct: (vendorId, productVariantId) => {
        set((state) => {
          const updatedItems = [...state.items];
          const cartItemIndex = updatedItems.findIndex(
            (i) => i.vendor.id === vendorId
          );
          if (cartItemIndex !== -1) {
            const productItemIndex = updatedItems[
              cartItemIndex
            ].products.findIndex(
              (p) => p.productVariantId === productVariantId
            );
            if (productItemIndex !== -1) {
              const products = state.items[cartItemIndex].products;
              products.splice(productItemIndex, 1);
              updatedItems[cartItemIndex] = {
                vendor: updatedItems[cartItemIndex].vendor,
                products,
              };
            }
          }
          return { items: updatedItems };
        });
      },
      reset: () => {
        set(() => ({
          items: [],
        }));
      },
    }),

    {
      name: 'cart',
    }
  )
);

export const selectResetCart = (state: CartStore) => state.reset;
export const selectIncreaseCart = (state: CartStore) => state.increaseItem;
export const selectDecreaseCart = (state: CartStore) => state.decreaseItem;
export const selectRemoveAllProductsCart = (state: CartStore) =>
  state.removeAllProducts;
export const selectRemoveProductCart = (state: CartStore) =>
  state.removeProduct;
export const selectCartItems = (state: CartStore) => state.items;
